"use client";

import { motion, type Variants } from "framer-motion";
import { ReactNode } from "react";

/**
 * Shared scroll-reveal wrapper. Fades + rises the child into view once.
 * `i` staggers siblings; pass an incrementing index. Keeps every section
 * component small by centralising the reveal motion in one place.
 */
const variants: Variants = {
  hidden: { opacity: 0, y: 42 },
  show: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.85, delay: i * 0.09, ease: [0.16, 1, 0.3, 1] },
  }),
};

// Explicit map avoids `motion(stringTag)`, which is not a supported call form.
const TAGS = {
  div: motion.div,
  h2: motion.h2,
  h3: motion.h3,
  p: motion.p,
  span: motion.span,
} as const;

type RevealProps = {
  children: ReactNode;
  i?: number;
  as?: keyof typeof TAGS;
  className?: string;
};

export default function Reveal({
  children,
  i = 0,
  as = "div",
  className,
}: RevealProps) {
  const Comp = TAGS[as];
  return (
    <Comp
      custom={i}
      variants={variants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      className={className}
    >
      {children}
    </Comp>
  );
}
